/* src/Pages/PreOrder/PreOrderUpload/index.js */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./styles.css";
import UserForm from "./components/UserForm";
import ImageUploadSection from "./components/ImageUploadSection";
import useFormSubmission from "./hooks/useFormSubmission";
import SubmissionFeedbackModal from "./components/SubmissionFeedbackModal";
import { SERVER_URL } from "../../../config/serverUrl";

const PreOrderUploadPage = () => {
  const sendWhatsappForHelp = false;
  const { eventName } = useParams();
  const [formConfig, setFormConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Load configuration
  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const serverUrl = SERVER_URL;
        const response = await fetch(
          `${serverUrl}/api/event-form-config?event_name=${encodeURIComponent(
            eventName
          )}`
        );

        if (response.ok) {
          const data = await response.json();
          setFormConfig(data);
        } else {
          console.error("Failed to load form configuration");
          // In case of error - leave formConfig as null
        }
      } catch (error) {
        console.error("Error loading form configuration:", error);
        // In case of error - leave formConfig as null
      } finally {
        setIsLoadingConfig(false);
      }
    };

    if (eventName) {
      loadFormConfig();
    }
  }, [eventName]);

  // Call hook with formConfig passed
  const {
    formData,
    imagePreview,
    faceValidation,
    showCamera,
    isSubmitting,
    isValidating,
    fileInputRef,
    handleInputChange,
    handleFileChange,
    openCamera,
    closeCamera,
    handleCapturedImage,
    removeImage,
    handleSubmit,
    formErrors,
    // New values
    showFeedbackModal,
    closeFeedbackModal,
  } = useFormSubmission(eventName, formConfig);

  // Component return
  if (isLoadingConfig) {
    return (
      <div className="preorder-upload-container">
        <div className="loading-spinner" />
        <p>טוען...</p>
      </div>
    );
  }

  // Instead of showing form with default values, show simple error message
  if (!formConfig) {
    return (
      <div className="preorder-upload-container">
        <h1 className="preorder-upload-title rtl">שגיאה בטעינת הדף</h1>
        <div className="config-error-container">
          <p className="config-error-message">
            מצטערים, לא הצלחנו לטעון את קובץ הטופס. אנא נסה שוב או פנה לצוות
            התמיכה במקרה הצורך.
          </p>
          <div className="config-error-buttons">
            <button
              onClick={() => window.location.reload()}
              className="retry-button"
            >
              לחץ כאן לנסיון חוזר
            </button>
            {sendWhatsappForHelp && (
              <a
                href="https://wa.me/972512822619?text=שלום, יש בעיה בטעינת טופס העלאת תמונה"
                target="_blank"
                rel="noopener noreferrer"
                className="support-button"
              >
                פנייה לתמיכה בוואטסאפ
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Check if image upload is enabled
  const showImageSection = formConfig.showImageSection !== false; // Default to true if not specified
  const imageRequired = formConfig.imageRequired !== false; // Default to true if not specified

  // Check if form can be submitted without an image
  const canSubmitForm = () => {
    if (
      (!imageRequired || (imagePreview && faceValidation.valid)) &&
      !isSubmitting &&
      !isValidating
    ) {
      for (const field of formConfig.fields || []) {
        const value = formData[field.id];

        if (field.type === "checkbox") {
          if (field.required && !value) return false;
        } else if (field.type === "select") {
          if (field.required) {
            if (!value || value === "") return false;
            const selectedOption = field.options?.find(
              (opt) => opt.value === value
            );
            if (selectedOption && selectedOption.allowSubmit === false)
              return false;
          }
        } else if (
          field.required &&
          (!value || (typeof value === "string" && value.trim() === ""))
        ) {
          return false;
        }
      }
      return true;
    }
    return false;
  };

  // Calculate logo width based on size setting
  const getLogoWidth = (size) => {
    const widths = {
      "-2": "20%",
      "-1": "35%",
      "0": "50%",
      "1": "65%",
      "2": "80%",
    };
    return widths[String(size)] || "50%";
  };

  // Get logo alignment style (RTL context: flex-start = right, flex-end = left)
  const getLogoAlignment = (alignment) => {
    const alignments = {
      left: "flex-end",
      center: "center",
      right: "flex-start",
    };
    return alignments[alignment] || "center";
  };

  return (
    <div className="preorder-upload-container">
      <h1 className="preorder-upload-title rtl">{formConfig.formTitle}</h1>

      {/* Logo section */}
      {formConfig.showLogo && formConfig.logoUrl && (
        <div
          className="preorder-logo-container"
          style={{ justifyContent: getLogoAlignment(formConfig.logoAlignment) }}
        >
          <img
            src={formConfig.logoUrl}
            alt="לוגו"
            className="preorder-logo"
            style={{ maxWidth: getLogoWidth(formConfig.logoSize) }}
          />
        </div>
      )}

      <form className="preorder-upload-form rtl" onSubmit={handleSubmit}>
        <UserForm
          formData={formData}
          onChange={handleInputChange}
          formConfig={formConfig}
          errors={formErrors}
        />

        {/* Only show image upload section if enabled in config */}
        {showImageSection && (
          <ImageUploadSection
            imagePreview={imagePreview}
            faceValidation={faceValidation}
            showCamera={showCamera}
            isValidating={isValidating}
            fileInputRef={fileInputRef}
            onGallerySelect={() => fileInputRef.current.click()}
            onFileChange={handleFileChange}
            onOpenCamera={openCamera}
            onCloseCamera={closeCamera}
            onCapturedImage={handleCapturedImage}
            onRemoveImage={removeImage}
            imageRequired={imageRequired}
          />
        )}

        <button
          type="submit"
          className="submit-button"
          disabled={!canSubmitForm()}
        >
          {isSubmitting ? "שולח..." : "שלח טופס"}
        </button>
      </form>

      {/* Add the feedback modal */}
      <SubmissionFeedbackModal
        isOpen={showFeedbackModal}
        onClose={closeFeedbackModal}
        imagePreview={imagePreview}
        formData={formData}
        formConfig={formConfig}
        showImageSection={showImageSection}
        showEventButton={formConfig?.showEventButton}
      />
    </div>
  );
};

export default PreOrderUploadPage;
